import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';

import MuiDialog from '@material-ui/core/Dialog';
import MuiAppBar from '@material-ui/core/AppBar';
import MuiCloseIcon from '@material-ui/icons/Close';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import MuiToolbar from '@material-ui/core/Toolbar';
import MuiIconButton from '@material-ui/core/IconButton';
import MuiSlide from '@material-ui/core/Slide';
import MuiTypography from '@material-ui/core/Typography';
import Button from 'Components/UI/Button';

import closeModal from 'Helpers/ModalHelper';
import { dataAttrs } from 'Helpers/Testing';

const SlideUp = React.forwardRef((props, ref) => (
  <MuiSlide direction="up" ref={ref} {...props} />
));

function FullscreenDialog({
  title,
  classes,
  children,
  variant,
  onClose,
  CloseButtonProps,
  className,
  hasBackButton,
  subtitle: subtitleProp,
  shouldCloseModal,
  stickyHeader,
  ...rest
}) {
  const [subtitle, setSubtitle] = useState(subtitleProp);

  return (
    <MuiDialog
      classes={{
        paper: classNames(classes.dialogPaper, className, {
          [classes.dialogPaperVariantGray]: variant === 'gray',
          [classes.dialogPaperVariantBlue]: variant === 'blue',
          [classes.dialogPaperVariantWhite]: variant === 'white',
        }),
      }}
      fullScreen
      onClose={() => {
        onClose();
        if (shouldCloseModal) {
          closeModal();
        }
      }}
      open
      TransitionComponent={SlideUp}
      {...rest}
    >
      <MuiAppBar
        elevation={0}
        className={classNames(classes.appBar, {
          [classes.stickyHeader]: stickyHeader,
        })}
      >
        <MuiToolbar
          className={classes.toolbar}
        >
          {hasBackButton ? (
            <Button
              className={classNames(classes.closeButton, classes.backButton)}
              startIcon={<ArrowBackIosIcon />}
              onClick={onClose}
              {...CloseButtonProps}
            >
              {'Back'}
            </Button>
          ) : (
            <MuiIconButton
              className={classes.closeButton}
              color="inherit"
              {...dataAttrs('close-button')}
              onClick={() => {
                onClose();
                if (shouldCloseModal) {
                  closeModal();
                }
              }}
              aria-label="close"
              {...CloseButtonProps}
            >
              <MuiCloseIcon />
            </MuiIconButton>)
          }
          <div className={classes.titleHolder}>
            <MuiTypography variant="h2" className={classes.title}>
              {title}
            </MuiTypography>
            <MuiTypography variant="h5" className={classes.subtitle}>
              {subtitle}
            </MuiTypography>
          </div>
        </MuiToolbar>
      </MuiAppBar>
      {React.cloneElement(children, {
        className: classes.dialogContent,
        setSubtitle,
      })}
    </MuiDialog>
  );
}

FullscreenDialog.defaultProps = {
  variant: 'blue',
  onClose: () => { },
  CloseButtonProps: {},
  className: '',
  hasBackButton: false,
  stickyHeader: false,
  subtitle: '',
  shouldCloseModal: true,
  centeredContent: true,
};

FullscreenDialog.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['white', 'blue', 'gray']),
  onClose: PropTypes.func,
  CloseButtonProps: PropTypes.object,
  className: PropTypes.string,
  subtitle: PropTypes.string,
  hasBackButton: PropTypes.bool,
  shouldCloseModal: PropTypes.bool,
  centeredContent: PropTypes.bool,
  stickyHeader: PropTypes.bool,
};

export default withStyles(theme => ({
  dialogPaper: {
    position: 'relative',
  },
  dialogPaperVariantGray: {
    background: theme.palette.facet.backgroundGray,
  },
  dialogPaperVariantBlue: {
    background: theme.gradients.vertical.primaryToAccentBlue,
  },
  dialogPaperVariantWhite: {
    backgroundColor: theme.palette.common.white,
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  appBar: {
    position: 'relative',
    backgroundColor: theme.palette.facet.backgroundGray,
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
  },
  toolbar: {
    justifyContent: 'center',
  },
  closeButton: {
    color: theme.palette.facet.primaryGray,
    position: 'absolute',
    left: 0,
  },
  backButton: {
    left: theme.spacing(1),
  },
  titleHolder: {
    textAlign: 'center',
    width: '100%',
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.facet.accentBlue19,
    textAlign: 'center',
  },
  subtitle: {
    fontWeight: theme.typography.fontWeightLight,
    color: theme.palette.common.white,
    textAlign: 'center',
  },
  [theme.breakpoints.down('md')]: {
    dialogPaper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    dialogContent: {
      marginTop: theme.spacing(8),
    },
  },
  [theme.breakpoints.up('lg')]: {
    dialogContent: {
      position: ({ centeredContent }) => ((centeredContent) ? 'absolute' : undefined),
      top: ({ centeredContent }) => ((centeredContent) ? '50%' : undefined),
      left: ({ centeredContent }) => ((centeredContent) ? '50%' : undefined),
      transform: ({ centeredContent }) => ((centeredContent) ? 'translate(-50%, -50%)' : undefined),
    },
  },
}))(FullscreenDialog);
